import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { useTranslation } from '../i18n';

/**
 * PWA Install prompt banner.
 * Shows when the browser fires 'beforeinstallprompt' (Chrome/Edge on Android),
 * which indicates the PWA meets install criteria.
 * On iOS, shows a manual instruction hint.
 */
export default function InstallBanner() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already dismissed recently
    try {
      const dismissedAt = localStorage.getItem('pwa-install-dismissed');
      if (dismissedAt && Date.now() - parseInt(dismissedAt) < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
        return;
      }
    } catch {}

    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone === true) return;

    // Detect iOS
    const ua = window.navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(isiOS);

    if (isiOS) {
      // On iOS, show hint to use Share > Add to Home Screen
      setShowBanner(true);
      return;
    }

    // Listen for beforeinstallprompt (Android/Chrome)
    const handlePrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handlePrompt);
    return () => window.removeEventListener('beforeinstallprompt', handlePrompt);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    try { localStorage.setItem('pwa-install-dismissed', Date.now().toString()); } catch {}
  };

  if (!showBanner || dismissed) return null;

  return (
    <div style={styles.banner}>
      <div style={styles.content}>
        <Download size={20} color="#0c4a6e" />
        <div style={styles.text}>
          {isIOS ? (
            <>
              <strong>{t('install.titleVIK')}</strong>
              <span style={styles.hint}>
                {t('install.iosHintPrefix')} <strong>{t('install.iosShare')}</strong> {t('install.iosHintMiddle')} <strong>{t('install.iosAddHome')}</strong>
              </span>
            </>
          ) : (
            <>
              <strong>{t('install.installApp')}</strong>
              <span style={styles.hint}>{t('install.hint')}</span>
            </>
          )}
        </div>
      </div>
      <div style={styles.actions}>
        {!isIOS && (
          <button onClick={handleInstall} style={styles.installBtn}>{t('install.button')}</button>
        )}
        <button onClick={handleDismiss} style={styles.dismissBtn}>
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

const styles = {
  banner: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: '#fff',
    borderTop: '1px solid #e2e8f0',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 900,
    boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
    gap: 10,
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: 13,
    color: '#334155',
  },
  hint: {
    fontSize: 11,
    color: '#64748b',
    marginTop: 1,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  installBtn: {
    padding: '6px 14px',
    background: '#0c4a6e',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  dismissBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#94a3b8',
    padding: 4,
    display: 'flex',
  },
};
